import { pollInterval } from '../realtime/live-updates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CollectionMeta,
  Conversation,
  ConversationListQuery,
  Message,
  MessageListQuery,
  PatchConversationInput,
  SendMessageInput,
  UploadConversationMediaResult,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['conversations'] as const;

export interface ConversationPage {
  items: Conversation[];
  meta: CollectionMeta;
}
export interface MessagePage {
  items: Message[];
  meta: CollectionMeta;
}

function listQuery(q: ConversationListQuery): Record<string, string | number | undefined> {
  return {
    page: q.page,
    pageSize: q.pageSize,
    status: q.status,
    assignedUserId: q.assignedUserId,
    channelId: q.channelId,
    unreadOnly: q.unreadOnly ? 'true' : undefined,
    search: q.search,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
  };
}

export async function listConversations(q: ConversationListQuery = {}): Promise<ConversationPage> {
  const res = await apiClient.getCollection<Conversation>('/conversations', {
    query: listQuery(q),
  });
  return { items: res.data, meta: res.meta };
}
export const getConversation = (id: string) => apiClient.get<Conversation>(`/conversations/${id}`);
export async function listMessages(id: string, q: MessageListQuery = {}): Promise<MessagePage> {
  const res = await apiClient.getCollection<Message>(`/conversations/${id}/messages`, {
    query: { page: q.page, pageSize: q.pageSize },
  });
  return { items: res.data, meta: res.meta };
}
export const sendMessage = (id: string, input: SendMessageInput, idempotencyKey: string) =>
  apiClient.post<Message>(`/conversations/${id}/messages`, input, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
export const markConversationRead = (id: string) =>
  apiClient.post<Conversation>(`/conversations/${id}/read`);
export const patchConversation = (id: string, patch: PatchConversationInput) =>
  apiClient.patch<Conversation>(`/conversations/${id}`, patch);
/**
 * On-demand media download: fetched fresh from Meta on every call (nothing
 * is cached server-side yet - see PROGRESS.md). Returns a `Blob` + whatever
 * filename the server derived, ready to hand to the browser's save dialog.
 */
export const downloadMessageMedia = (conversationId: string, messageId: string) =>
  apiClient.getBlob(`/conversations/${conversationId}/messages/${messageId}/media`);

/** Uploads an image to attach (Inbox composer's "attach a photo" flow) - returns a `mediaId` to pass into `sendMessage` as `{ messageType: 'IMAGE', mediaId, caption? }`. */
export const uploadConversationMedia = (conversationId: string, file: File) => {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiClient.post<UploadConversationMediaResult>(
    `/conversations/${conversationId}/media`,
    form,
  );
};

/**
 * New messages normally arrive within a second via live updates
 * (`realtime/live-updates.ts`, SSE). This poll is the safety net: every 8s
 * while the live stream is down, relaxing to 60s while it's up. React Query
 * only keeps it running while the Inbox is actually mounted.
 */
const INBOX_POLL_MS = 8_000;

export function useConversations(query: ConversationListQuery = {}) {
  return useQuery<ConversationPage, ApiClientError>({
    queryKey: [...KEY, 'list', query],
    queryFn: () => listConversations(query),
    refetchInterval: pollInterval(INBOX_POLL_MS),
  });
}

export function useConversation(id: string | undefined) {
  return useQuery<Conversation, ApiClientError>({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getConversation(id!),
    enabled: Boolean(id),
  });
}

export function useMessages(id: string | undefined) {
  return useQuery<MessagePage, ApiClientError>({
    queryKey: [...KEY, 'messages', id],
    queryFn: () => listMessages(id!),
    enabled: Boolean(id),
    refetchInterval: pollInterval(INBOX_POLL_MS),
    // Always fetch fresh on switching into a chat, even one already cached
    // within the global 30s staleTime - "I navigated here" should never show
    // stale messages while the background poll above catches up.
    refetchOnMount: 'always',
  });
}

export function useSendMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation<Message, ApiClientError, SendMessageInput>({
    mutationFn: (input) => sendMessage(conversationId, input, globalThis.crypto.randomUUID()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'messages', conversationId] });
      void qc.invalidateQueries({ queryKey: [...KEY, 'detail', conversationId] });
      void qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

/** Uploads the file, then sends it as an `IMAGE` message with an optional caption - the Inbox composer's single "attach + send" action. */
export function useSendImageMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation<Message, ApiClientError, { file: File; caption: string }>({
    mutationFn: async ({ file, caption }) => {
      const { mediaId } = await uploadConversationMedia(conversationId, file);
      return sendMessage(
        conversationId,
        { messageType: 'IMAGE', mediaId, caption: caption.trim() || undefined },
        globalThis.crypto.randomUUID(),
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...KEY, 'messages', conversationId] });
      void qc.invalidateQueries({ queryKey: [...KEY, 'detail', conversationId] });
      void qc.invalidateQueries({ queryKey: [...KEY, 'list'] });
    },
  });
}

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation<Conversation, ApiClientError, string>({
    mutationFn: markConversationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Downloads a message's media attachment and hands it to the browser's own
 * save-file flow (a temporary object URL + a synthetic `<a download>` click -
 * the standard way to save a fetched `Blob`, since a plain `<a href>` can't
 * carry the Authorization header this endpoint needs).
 */
export function useDownloadMessageMedia(conversationId: string) {
  return useMutation<void, ApiClientError, string>({
    mutationFn: async (messageId) => {
      const { blob, fileName } = await downloadMessageMedia(conversationId, messageId);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName ?? 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        URL.revokeObjectURL(url);
      }
    },
  });
}

export function usePatchConversation(conversationId: string) {
  const qc = useQueryClient();
  return useMutation<Conversation, ApiClientError, PatchConversationInput>({
    mutationFn: (patch) => patchConversation(conversationId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
